import { Injectable } from '@angular/core';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BackendService } from './backend.service';
import { environment } from '../../environments/environment';

enum FileType {
  TEXT_TAB = 'text_tab',
  IMAGE_TAB = 'image_tab',
  PDF_BROWSER = 'pdf_browser',
  HTML_BROWSER = 'html_browser',
  DOWNLOAD_ONLY = 'download_only',
}

export interface FileDownloadOptions {
  fileName?: string;
  forceDownload?: boolean;
}

export interface ContentWindowOptions {
  sample?: string;
  title?: string;
  contentType: 'text' | 'image';
}

@Injectable({
  providedIn: 'root',
})
export class SecureFileService {
  constructor(private backendService: BackendService) {}

  /**
   * Centralized file type classification
   */
  private classifyFileType(fileExtension: string): FileType {
    if (!fileExtension) return FileType.DOWNLOAD_ONLY;

    switch (fileExtension.toLowerCase()) {
      case 'txt':
        return FileType.TEXT_TAB;
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'svg':
        return FileType.IMAGE_TAB;
      case 'pdf':
        return FileType.PDF_BROWSER;
      case 'html':
        return FileType.HTML_BROWSER;
      default:
        return FileType.DOWNLOAD_ONLY;
    }
  }

  /**
   * Extract file extension from path
   */
  private extractFileExtension(filePath: string): string {
    const lastDotIndex = filePath.lastIndexOf('.');
    return lastDotIndex !== -1 ? filePath.substring(lastDotIndex + 1) : '';
  }

  /**
   * Extract filename from path
   */
  private extractFilename(filePath: string): string {
    const lastSlashIndex = Math.max(filePath.lastIndexOf('/'), filePath.lastIndexOf('\\'));
    return lastSlashIndex !== -1 ? filePath.substring(lastSlashIndex + 1) : filePath;
  }

  /**
   * Extract event data from cell click
   */
  private extractEventData(event: any): { value: string; sample: string; text: string } {
    return {
      value: event.target.getAttribute('data-link'),
      sample: event.target.getAttribute('data-sample'),
      text: event.target.textContent,
    };
  }

  /**
   * Entry point: Downloads a file securely with centralized file type handling
   */
  downloadFile(filePath: string, options: FileDownloadOptions = {}): Observable<void> {
    const cleanPath = filePath.replace('s3_redirect/', '');
    const fileExtension = this.extractFileExtension(cleanPath);
    const fileType = this.classifyFileType(fileExtension);

    const filename = options.fileName || this.extractFilename(cleanPath);
    const context = {
      filename: filename,
      title: filename,
    };

    // Pass file path directly to routing - let handlers manage their own S3 calls
    this.routeFileHandling(cleanPath, fileType, context);

    // Return completed observable since file handling is async via routing
    return of(undefined);
  }

  /**
   * Entry point: Handles cell click events with centralized file type handling
   */
  handleCellClick(event: any): void {
    const { value, sample, text } = this.extractEventData(event);

    if (!value) {
      console.warn('No data-link attribute found');
      return;
    }

    const filePath = value.replace('s3_redirect/', '');
    const filename = this.extractFilename(filePath);
    const fileExtension = this.extractFileExtension(filename);
    const fileType = this.classifyFileType(fileExtension);

    const context = {
      filename: filename,
      title: sample ? `${text} for sample ${sample}` : text,
      text: text,
    };

    this.routeFileHandling(filePath, fileType, context);
  }

  /**
   * Central router for file handling based on file type
   */
  private routeFileHandling(filePath: string, fileType: FileType, context: any): void {
    switch (fileType) {
      case FileType.TEXT_TAB:
        this.handleTextInNewTab(filePath, context);
        break;
      case FileType.IMAGE_TAB:
        this.handleImageInNewTab(filePath, context);
        break;
      case FileType.PDF_BROWSER:
      case FileType.HTML_BROWSER:
        this.handleBrowserOpen(filePath);
        break;
      case FileType.DOWNLOAD_ONLY:
        this.handleDirectDownload(filePath, context.filename);
        break;
    }
  }

  /**
   * Opens content in a new themed browser tab
   */
  openInNewTab(title: string, content: string, contentType: 'text' | 'image'): void {
    const externalWindow = window.open('', '_blank');
    if (!externalWindow) {
      alert('New tab blocked. Please allow new tabs for this site.');
      return;
    }

    this.setupWindowTheme(externalWindow);
    externalWindow.document.title = title;

    const containerHtml = this.generateContentHtml(title, content, contentType);
    externalWindow.document.body.innerHTML = containerHtml;
  }

  /**
   * Specialized handler: Text files in new tabs
   */
  private handleTextInNewTab(filePath: string, context: any): void {
    this.backendService.getS3Content(filePath).subscribe({
      next: (response: any) => {
        if (response.status === 200) {
          this.openInNewTab(context.title, response.reply.content, 'text');
        } else {
          alert('Failed to load content. Please try again.');
        }
      },
      error: (error) => this.handleFileError(error, 'Failed to load text content'),
    });
  }

  /**
   * Specialized handler: Images in new tabs
   */
  private handleImageInNewTab(filePath: string, context: any): void {
    this.backendService.getS3Link(filePath).subscribe({
      next: (response: any) => {
        if (response.status === 200 && response.reply && response.reply.link) {
          this.openInNewTab(context.title, response.reply.link, 'image');
        } else {
          alert('Failed to load image. Please try again.');
        }
      },
      error: (error) => this.handleFileError(error, 'Failed to load image'),
    });
  }

  /**
   * Specialized handler: PDFs and HTML in browser tabs
   */
  private handleBrowserOpen(filePath: string): void {
    this.backendService.getS3Link(filePath).subscribe({
      next: (response: any) => {
        if (response.status === 200 && response.reply && response.reply.link) {
          window.open(response.reply.link, '_blank');
        } else {
          this.triggerDirectDownload(environment.serverUrl + 's3_redirect/' + filePath);
        }
      },
      error: (error) => {
        console.error('Failed to get signed URL:', error);
        if (error.status === 401) {
          alert('Session expired. Please refresh the page and try again.');
        } else {
          this.triggerDirectDownload(environment.serverUrl + 's3_redirect/' + filePath);
        }
      },
    });
  }

  /**
   * Specialized handler: Direct downloads
   */
  private handleDirectDownload(filePath: string, fileName?: string): void {
    this.backendService.getS3Link(filePath).subscribe({
      next: (response: any) => {
        if (response.status === 200 && response.reply && response.reply.link) {
          this.triggerDirectDownload(response.reply.link, fileName);
        } else {
          this.triggerDirectDownload(environment.serverUrl + 's3_redirect/' + filePath, fileName);
        }
      },
      error: (error) => {
        console.error('Failed to get signed URL:', error);
        if (error.status === 401) {
          alert('Session expired. Please refresh the page and try again.');
        } else {
          this.triggerDirectDownload(environment.serverUrl + 's3_redirect/' + filePath, fileName);
        }
      },
    });
  }

  /**
   * Handles file loading errors with appropriate user feedback
   */
  private handleFileError(error: any, message: string): void {
    console.error(message, error);
    if (error.status === 401) {
      alert('Session expired. Please refresh the page and try again.');
    } else {
      alert('Failed to load content. Please try again.');
    }
  }

  /**
   * Triggers direct download with _self target
   */
  private triggerDirectDownload(url: string, fileName?: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_self';
    if (fileName) {
      link.download = fileName;
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Sets up theme and styles for new tabs
   */
  private setupWindowTheme(externalWindow: Window): void {
    // Copy styles to new window
    document.querySelectorAll('link, style').forEach((htmlElement) => {
      externalWindow.document.head.appendChild(htmlElement.cloneNode(true));
    });

    // Apply current theme
    const theme = localStorage.getItem('theme');
    if (theme) {
      externalWindow.document.documentElement.setAttribute('data-bs-theme', theme);
      if (theme === 'dark') {
        externalWindow.document.documentElement.classList.add('dark');
      } else {
        externalWindow.document.documentElement.classList.remove('dark');
      }
    }
  }

  /**
   * Generates HTML content for new tabs
   */
  private generateContentHtml(title: string, content: string, contentType: 'text' | 'image'): string {
    if (contentType === 'image') {
      return `
        <div class="container-fluid" style="width: 100%; padding-left: 40px; padding-right: 40px; padding-top: 25px">
          <h2>${title}</h2>
          <img src="${content}" style="border: none; max-width: 100%; height: auto;" alt="${title}">
        </div>`;
    } else {
      return `
        <div class="container-fluid" style="width: 100%; padding-left: 40px; padding-right: 40px; padding-top: 25px">
          <h2>${title}</h2>
          <pre><code>${content}</code></pre>
        </div>`;
    }
  }
}
